/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd. <legal@vecros.com>
 * Proprietary and confidential.
 **********************************************************************************/

import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import MessageBubble from "./MessageBubble";
import EmptyState from "./EmptyState";
import { useAssistant } from "../AssistantContext";

/** Scroll region: empty state when no messages, else the message list. Autoscrolls. */
export default function ThreadView({ userName }) {
  const { messages, send } = useAssistant();
  const bottomRef = useRef(null);
  const pinnedRef = useRef(true);
  const scrollRef = useRef(null);

  // Track whether the user is near the bottom; only autoscroll when they are.
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  useEffect(() => {
    if (pinnedRef.current && bottomRef.current) {
      bottomRef.current.scrollIntoView({ block: "end" });
    }
  }, [messages]);

  if (messages.length === 0) {
    return (
      <Box sx={{ flex: 1, display: "flex", overflow: "auto" }}>
        <EmptyState userName={userName} onPick={(p) => send(p)} />
      </Box>
    );
  }

  return (
    <Box
      ref={scrollRef}
      onScroll={onScroll}
      aria-live="polite"
      sx={{ flex: 1, overflow: "auto", px: 1.5, py: 1.5 }}
    >
      {messages.map((m) => (
        <MessageBubble key={m.id} message={m} />
      ))}
      <div ref={bottomRef} />
    </Box>
  );
}
